/**
 * What a coin looks like, as a list of rectangles.
 *
 * DRAWN RATHER THAN LOADED, and that is a deliberate choice over a sprite
 * sheet. A coin at this scale is seven pixels across: an art asset for it would
 * need a generator script, a manifest entry, a load state, a load-failure
 * fallback and a place in `.gitignore`, to describe a shape that fits in five
 * `fillRect` calls. The engine already draws its sign posts, portal markers,
 * shadows and walk targets exactly this way.
 *
 * NO `arc()`, EVER. The art canvas is 320x180 upscaled 3x with
 * `imageRendering: pixelated`, so a circle's antialiased edge — a ring of
 * partial-alpha grey pixels — is magnified into a visible three-pixel fringe.
 * Every coordinate returned from here is a whole number for the same reason,
 * which is a rule a test can actually check.
 *
 * PURE, so it can be. Same move that put zoneShapes.js and closedLabel.js
 * outside the engine: the decision lives where it can be pinned, and
 * GameEngine keeps only the `fillRect` loop.
 */

/** Three tones read as metal. Two read as a sticker. */
const RIM = '#8A5A1E';
const BODY = '#D4A24C'; // the project's gold — matches the first player colour
const GLINT = '#F5D97A'; // the prompt gold

/**
 * One bob, in milliseconds.
 *
 * Deliberately not a multiple of the portal pulse (~2.4s), so a coin sitting on
 * a portal does not beat against it.
 */
export const COIN_BOB_MS = 1400;

/** Fraction of the cycle the highlight is lit. */
const GLINT_FRACTION = 0.15;

/**
 * How far above its resting position a coin sits right now.
 *
 * WHOLE PIXELS ONLY, hence the round. A fractional offset would resample the
 * whole shape every frame and shimmer under the 3x upscale — the exact thing
 * `Camera.toScreenX` rounds to avoid.
 *
 * @param {number} nowMs
 * @param {number} id - the coin's id, used only to offset its phase
 */
export function coinBob(nowMs, id) {
  // Per-coin phase so a field of coins does not bob in lockstep. 137 is coprime
  // with the cycle, the same trick `phaseFor` uses for sprite animation — and
  // coin ids are never recycled, so a phase can never follow an id to a
  // different coin.
  const phase = (id * 137) % COIN_BOB_MS;
  return Math.round(Math.sin(((nowMs + phase) / COIN_BOB_MS) * Math.PI * 2));
}

/**
 * The coin, as `[x, y, width, height, colour]` offsets from its centre.
 *
 * A 7x7 octagon — round enough to read as a coin, flat enough to have hard
 * edges:
 *
 *     ..###..
 *     .#####.
 *     #######
 *     #######
 *     #######
 *     .#####.
 *     ..###..
 *
 * @param {number} nowMs
 * @param {number} id
 * @returns {[number, number, number, number, string][]}
 */
export function coinRects(nowMs, id) {
  const dy = coinBob(nowMs, id);
  const phase = (id * 137) % COIN_BOB_MS;

  const rects = [
    [-1, dy - 3, 3, 1, RIM],
    [-2, dy - 2, 5, 1, RIM],
    [-3, dy - 1, 7, 3, RIM],
    [-2, dy + 2, 5, 1, RIM],
    [-1, dy + 3, 3, 1, RIM],
    // The face, inset so the rim reads as an edge rather than an outline.
    [-2, dy - 1, 5, 3, BODY],
  ];

  // A glint for a moment each cycle. The only moving highlight on an otherwise
  // static floor, which is what makes a coin findable at a glance — the same
  // argument the portal marker's pulse makes for itself.
  if ((nowMs + phase) % COIN_BOB_MS < COIN_BOB_MS * GLINT_FRACTION) {
    rects.push([-1, dy - 1, 1, 2, GLINT]);
  }

  return rects;
}

export default coinRects;
